'use client'

import logoImage from '@/assets/frage_walter_scout_logo.png'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { APP_BRAND_NAME } from '@/consts/branding'
import { APP_ROUTES } from '@/consts/routes'
import { AuthRequestError, useAuth } from '@/provider/AuthContext'
import { Loader2, Lock, Mail } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'

/**
 * Handles login form state and renders the full auth split view.
 */
const AuthFormView = () => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')

    const { login, user } = useAuth()
    const router = useRouter()
    const searchParams = useSearchParams()

    useEffect(() => {
        if (user) {
            router.push(resolveAuthRedirectTarget(searchParams.get('next')))
        }
    }, [user, router, searchParams])

    // TODOs - CODEX
    // Registration mode is intentionally disabled.
    // Keep this placeholder for future reactivation of register via URL mode.
    // const searchParams = useSearchParams()
    // const [isRegister, setIsRegister] = useState(searchParams.get('mode') === 'register')
    // useEffect(() => {
    //     setIsRegister(searchParams.get('mode') === 'register')
    // }, [searchParams])

    /**
     * Submits authentication data and waits for session synchronization.
     */
    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault()
        setError('')
        setIsLoading(true)

        try {
            await login(email, password)
        } catch (authError) {
            setError(getAuthErrorMessage(authError))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-background flex c">
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <Link href={APP_ROUTES.home} className="flex items-center gap-2 mb-8">
                        <Image src={logoImage} alt={APP_BRAND_NAME} height={40} className="w-auto" priority />
                    </Link>

                    <h1 data-testid="auth-heading" className="text-3xl font-bold text-foreground mb-2">
                        Willkommen zurück
                    </h1>
                    <p className="text-muted-foreground mb-8">
                        Melden Sie sich an, um Ihr Unternehmensprofil zu verwalten.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* TODO CODEX
                            Registration-specific company name field is intentionally disabled.
                            <div className="space-y-2">
                                <Label htmlFor="companyName">Unternehmensname</Label>
                                <div className="relative">
                                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="companyName"
                                        type="text"
                                        placeholder="Muster GmbH"
                                        value={companyName}
                                        onChange={(event) => setCompanyName(event.target.value)}
                                        className="pl-10"
                                        required
                                    />
                                </div>
                            </div>
                        */}

                        <div className="space-y-2">
                            <Label htmlFor="email">E-Mail</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    id="email"
                                    data-testid="auth-email-input"
                                    type="email"
                                    placeholder="kontakt@unternehmen.de"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    className="pl-10"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Passwort</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    id="password"
                                    data-testid="auth-password-input"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(event) => setPassword(event.target.value)}
                                    className="pl-10"
                                    required
                                    minLength={6}
                                />
                            </div>
                        </div>

                        {error ? (
                            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
                        ) : null}

                        <Button
                            data-testid="auth-submit-button"
                            type="submit"
                            className="w-full"
                            size="lg"
                            disabled={isLoading}
                        >
                            {isLoading ? <Loader2 className="mr-2 w-4 h-4 animate-spin" /> : null}
                            Anmelden
                        </Button>
                    </form>

                    {/* TODO CODEX
                        Registration switch is intentionally disabled.
                        <p className="mt-6 text-center text-muted-foreground">
                            Noch kein Konto? Registrieren
                        </p>
                    */}
                </div>
            </div>

            <div className="hidden lg:flex flex-1 hero-gradient items-center justify-center p-12">
                <div className="max-w-md text-primary-foreground text-center">
                    <div className="mx-auto mb-8 flex items-center justify-center">
                        <Image
                            src={logoImage}
                            alt={APP_BRAND_NAME}
                            height={64}
                            className="w-auto brightness-0 invert"
                            priority
                        />
                    </div>
                    <h2 className="text-3xl font-bold mb-4">Finden Sie die besten Talente</h2>
                    <p className="text-primary-foreground/80 text-lg">
                        Präsentieren Sie Ihr Unternehmen und Ihre Ausbildungsangebote auf unserer
                        Berufsorientierungsplattform für Schüler.
                    </p>
                </div>
            </div>
        </div>
    )
}

export default AuthFormView

/**
 * Maps auth request failures to user-facing messages.
 */
const getAuthErrorMessage = (error: unknown) => {
    if (error instanceof AuthRequestError) {
        if (error.status === 401) {
            return 'Ungültige Zugangsdaten.'
        }
        if (error.status === 400) {
            return 'Ungültige Eingaben.'
        }
    }

    return 'Serverfehler. Bitte versuchen Sie es erneut.'
}

/**
 * Resolves the post-login target based on terms acceptance state and safe next paths.
 */
const resolveAuthRedirectTarget = (nextPath: string | null) => {
    return isSafeNextPath(nextPath) ? nextPath : APP_ROUTES.dashboard
}

/**
 * Validates relative next paths and prevents redirects to auth-only routes.
 */
const isSafeNextPath = (nextPath: string | null): nextPath is string => {
    if (!nextPath) {
        return false
    }

    if (!nextPath.startsWith('/') || nextPath.startsWith('//')) {
        return false
    }

    if (nextPath.startsWith(APP_ROUTES.auth)) {
        return false
    }

    return true
}
